import { forwardRef, type ButtonHTMLAttributes } from "react";
import { X } from "lucide-react";
import { GlassButton } from "./GlassButton";
import { ICON_STROKE } from "./icons/icon-tokens";

/**
 * CloseButton — the single, canonical "close this overlay" affordance
 * (audit UX-09).
 *
 * A round translucent `GlassButton` with the X glyph — the same control the
 * chat header and notification panels already use. Every modal / dialog /
 * drawer / sheet / popover header should render THIS instead of a bespoke
 * `<button><X/></button>`, so the close affordance is consistent everywhere
 * and restyling it (size, glass chrome, icon) is a one-file change in
 * `GlassButton` / here.
 *
 * Accepts all native button props (onClick, disabled, className via style is
 * discouraged — restyle the shared component instead). Defaults `aria-label`
 * to "Close".
 */
export const CloseButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
    /** Button diameter in px. Default 32 (matches GlassButton round). */
    size?: number;
  }
>(function CloseButton({ "aria-label": ariaLabel = "Close", size, ...rest }, ref) {
  const iconPx = size ? Math.round(size * 0.5) : 14;
  return (
    <GlassButton ref={ref} shape="round" size={size} aria-label={ariaLabel} {...rest}>
      <X style={{ width: iconPx, height: iconPx }} strokeWidth={ICON_STROKE} />
    </GlassButton>
  );
});
